// ===== 共有ドメイン型 =====
// 公開サイト / 管理画面 / Supabase 層すべてが参照する単一の型定義。

export type PostStatus = "draft" | "published";

export type EditorRole = "admin" | "editor";

export interface Category {
  id: string;
  name: string;
  /** 英字ラベル（「英語見出し+日本語サブ」2段表示用） */
  name_en: string;
  slug: string;
  description: string | null;
  sort_order: number;
  /** カテゴリページのヘッダー／一覧表示用カバー画像（任意） */
  cover_image: string | null;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
}

export interface Editor {
  id: string;
  email: string;
  display_name: string;
  role: EditorRole;
  avatar_url: string | null;
}

/**
 * 記事の本文ブロック。
 * 基本テンプレ（H2章 → 章扉写真 → Q&A）に加え、表現力を高める拡張ブロックを持つ。
 */
export type ArticleBlock =
  | { type: "heading"; text: string }            // H2 章見出し
  | { type: "paragraph"; text: string }          // 地の文 / リード補足
  | { type: "image"; url: string; caption?: string; alt?: string } // 章扉・本文写真（alt=代替テキスト）
  | { type: "qa"; question: string; answer: string } // Q&A（質問はネイビー太字で描画）
  | { type: "quote"; text: string; cite?: string; image?: string | null } // 引用・強調（image=背景画像）
  | { type: "list"; items: string[] }            // 箇条書き
  // ---- 拡張ブロック ----
  | { type: "embed"; provider: "youtube"; url: string; caption?: string } // 動画埋め込み
  | { type: "balloon"; side: "left" | "right"; name: string; avatar?: string | null; text: string } // 対談の吹き出し
  | { type: "callout"; title: string; text: string; tone?: "info" | "tip" | "note"; image?: string | null } // 補足ボックス（image=背景画像）
  | { type: "cta"; title: string; text?: string; buttonLabel: string; href: string; image?: string | null } // 記事内CTA（image=背景画像）
  | { type: "divider" }                          // 区切り線
  | { type: "table"; headers: string[]; rows: string[][] }; // 表

export type ArticleBlockType = ArticleBlock["type"];

export interface Post {
  id: string;
  title: string;
  slug: string;
  status: PostStatus;
  /** リード文（記事冒頭の導入） */
  lead: string;
  /** メインビジュアル */
  hero_image: string | null;
  /** 構造化された本文 */
  body: ArticleBlock[];
  category: Category | null;
  tags: Tag[];
  /** インタビュー対象者プロフィール（任意） */
  profile: PostProfile | null;
  author: Editor | null;
  reading_time: number; // 分
  /** 閲覧数（人気記事ランキング・分析に使用） */
  view_count: number;
  /** サイドバーのおすすめ順（1が先頭）。1本でも入っていれば閲覧数順の代わりにこの順で出す */
  ranking_order?: number | null;
  /** 予約公開の日時（未来日時なら公開サイトには出さない） */
  scheduled_at: string | null;
  /** 記事個別のSEO設定（未設定なら title/lead をフォールバック） */
  seo_title: string | null;
  seo_description: string | null;
  og_image: string | null;
  /** 記事詳細サイドの JOIN US CTA カード背景画像（未設定ならグラデーション） */
  join_us_image: string | null;
  published_at: string | null; // ISO
  created_at: string;
  updated_at: string;
}

export interface PostProfile {
  name: string;
  role: string;       // 職種
  department: string; // 部署
  joined_year: string; // 入社年
  photo: string | null;
}

// ===== 求人(Job)機能 =====
export type EmploymentType =
  | "fulltime"   // 正社員
  | "contract"   // 契約社員
  | "freelance"  // 業務委託
  | "parttime"   // アルバイト・パート
  | "intern";    // インターン

export type WorkStyle = "onsite" | "remote" | "hybrid";

export type JobStatus = "draft" | "published" | "closed";

export const EMPLOYMENT_TYPE_LABEL: Record<EmploymentType, string> = {
  fulltime: "正社員",
  contract: "契約社員",
  freelance: "業務委託",
  parttime: "アルバイト・パート",
  intern: "インターン",
};

export const WORK_STYLE_LABEL: Record<WorkStyle, string> = {
  onsite: "出社",
  remote: "フルリモート",
  hybrid: "ハイブリッド",
};

export interface Job {
  id: string;
  title: string;
  slug: string;
  status: JobStatus;
  employment_type: EmploymentType;
  department: string;
  location: string;
  work_style: WorkStyle;
  /** 給与表示用テキスト（例「年収450万〜800万円」）。min/maxは構造化データ用 */
  salary_text: string | null;
  salary_min: number | null;
  salary_max: number | null;
  summary: string;            // 募集概要（リード）
  /** 求人詳細の主役ビジュアル／一覧サムネイル（任意） */
  cover_image: string | null;
  description: ArticleBlock[]; // 仕事内容など本文ブロック
  requirements: string[];     // 必須要件
  welcome: string[];          // 歓迎要件
  benefits: string[];         // 待遇・福利厚生
  sort_order: number;
  /** 求人個別のSEO設定（未設定なら title/summary をフォールバック） */
  seo_title: string | null;
  seo_description: string | null;
  og_image: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobCard {
  id: string;
  title: string;
  slug: string;
  employment_type: EmploymentType;
  department: string;
  location: string;
  work_style: WorkStyle;
  salary_text: string | null;
  summary: string;
  cover_image: string | null;
}

export interface Application {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  desired_role: string | null;
  message: string | null;
  /** 応募元の求人（任意） */
  job_id: string | null;
  job_title: string | null;
  status: "new" | "in_review" | "contacted" | "closed";
  created_at: string;
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  status: "new" | "in_review" | "replied" | "closed";
  created_at: string;
}

// 一覧表示用の軽量カード型
export interface PostCard {
  id: string;
  title: string;
  slug: string;
  lead: string;
  hero_image: string | null;
  category: Pick<Category, "name" | "name_en" | "slug"> | null;
  tags: Pick<Tag, "name" | "slug">[];
  published_at: string | null;
  reading_time: number;
  view_count: number;
  ranking_order?: number | null;
}

// 検索結果
export interface SearchResults {
  query: string;
  posts: PostCard[];
  jobs: JobCard[];
}
